import { create } from 'zustand';
import type { Contact, Cove, Message, User } from '@ourcove/types';

interface IncomingCall {
  callId: string;
  from: string;
  fromName: string;
  sdp: string;
}

interface State {
  accessToken: string | null;
  user: User | null;
  cove: Cove | null;
  contacts: Contact[];
  messages: Record<string, Message[]>; // threadId → messages ASC
  wsConnected: boolean;
  incomingCall: IncomingCall | null;
  activeCallId: string | null;

  setAuth: (t: string, u: User, c: Cove) => void;
  clearAuth: () => void;
  updateUser: (patch: Partial<User>) => void;
  setContacts: (c: Contact[]) => void;
  setContactOnline: (userId: string, online: boolean) => void;
  addMessage: (m: Message) => void;
  setThreadMessages: (threadId: string, m: Message[]) => void;
  setWsConnected: (b: boolean) => void;
  setIncomingCall: (c: IncomingCall | null) => void;
  setActiveCall: (id: string | null) => void;
}

export const useStore = create<State>((set) => ({
  accessToken: null,
  user: null,
  cove: null,
  contacts: [],
  messages: {},
  wsConnected: false,
  incomingCall: null,
  activeCallId: null,

  setAuth: (t, u, c) => set({ accessToken: t, user: u, cove: c }),
  clearAuth: () =>
    set({
      accessToken: null,
      user: null,
      cove: null,
      contacts: [],
      messages: {},
      wsConnected: false,
      incomingCall: null,
      activeCallId: null,
    }),
  updateUser: (patch) =>
    set((s) => (s.user ? { user: { ...s.user, ...patch } } : {})),
  setContacts: (contacts) => set({ contacts }),
  setContactOnline: (userId, online) =>
    set((s) => ({
      contacts: s.contacts.map((c) => (c.id === userId ? { ...c, online } : c)),
    })),
  addMessage: (m) =>
    set((s) => {
      const list = s.messages[m.threadId] ?? [];
      if (list.some((x) => x.id === m.id)) return { messages: s.messages };
      return { messages: { ...s.messages, [m.threadId]: [...list, m] } };
    }),
  setThreadMessages: (threadId, list) =>
    set((s) => ({ messages: { ...s.messages, [threadId]: list } })),
  setWsConnected: (b) => set({ wsConnected: b }),
  setIncomingCall: (c) => set({ incomingCall: c }),
  setActiveCall: (id) => set({ activeCallId: id }),
}));

export function threadIdFor(a: string, b: string): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}
